import { useStreamV2OrderBookTopQuotes } from '@hooks/useStreamV2OrderBookTopQuotes';
import { useStreamV2Product } from '@hooks/useStreamV2Product';
import { OrderBookTopMessage } from '@protos/v2/orderBookTop';
import { Ticker, tickerMeta } from '@protos/v2/ticker';
import { useDashboardContext } from '@shared/contexts/DashboardContext';
import debounce from 'lodash/debounce';
import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { RollingRowSettings } from '../shared/types';
import { BidAskType } from './types';
import { PricingGrid, usePricingGridState } from './usePricingGridState';
import { isAfterSettlementTime } from '@shared/utils/date';
import { ProductType } from '@shared/protos/product';

type Props = {
  children: ReactNode;
  widgetId: string;
  initialValues: {
    selectedColumns: string[];
    selectedRows: string[];
    columnsOrder: string[];
    userRollingRowSettings: RollingRowSettings;
    isOverrideRolling: boolean;
    selectedBidAskColumns?: BidAskType[];
    isShowCurrencyUnit: boolean;
    columnWidths: Record<string, number>;
  };
  isFrozen?: boolean;
};

const PricesContext = createContext<PricingGrid | undefined>(undefined);
PricesContext.displayName = 'PricesContext';

export default function PricesProvider({ widgetId, initialValues, isFrozen, ...otherProps }: Props) {
  const {
    selectedColumns, // "Columns" actually means products
    selectedRows, // "Rows" actually means tenors
    columnsOrder: initialColumnsOrder, // Can be an empty array
    userRollingRowSettings, // 'is' means 'boolean' and is a naming convention that needs to go away
    isOverrideRolling: initialIsOverrideLoading, // As above.
    selectedBidAskColumns: initialSelectedBidAskColumns, // These should be flags. Only two valid values: 'bid' and 'ask'
    isShowCurrencyUnit: initialIsShowCurrencyUnit, // Christ
    columnWidths: initialColumnWidths, // TODO: refactor this, layout shouldn't be in this
  } = { ...initialValues };

  const gridState = usePricingGridState(
    selectedColumns,
    selectedRows,
    initialColumnsOrder,
    userRollingRowSettings,
    initialIsOverrideLoading,
    initialSelectedBidAskColumns || [],
    initialIsShowCurrencyUnit,
    initialColumnWidths
  );

  const {
    selectedColIds,
    selectedRowIds,
    columnsOrder,
    rollingRowSettings,
    isOverrideRolling,
    gridRef,
    productMap,
    tenorMap,
    selectedUserProducts,
    selectedBidAskColumns,
    isShowCurrencyUnit,
    columnWidths,
  } = gridState;

  const { editWidgetPayloadById } = useDashboardContext();
  const isFirstUnmountRef = useRef(true);
  const isInitialMount = useRef(true);

  const [batchedTickerUpdates, setBatchedTickerUpdates] = useState<Ticker[]>([]);
  const [batchedOrderBookUpdates, setBatchedOrderBookUpdates] = useState<OrderBookTopMessage[]>([]);
  const isFrozenRef = useRef(isFrozen);
  const [forceResubscription, setForceResubscription] = useState(0);

  useEffect(() => {
    isFrozenRef.current = isFrozen;
  }, [isFrozen]);

  const productMapRef = useRef(productMap);
  const tenorMapRef = useRef(tenorMap);

  useEffect(() => {
    productMapRef.current = productMap;
  }, [productMap]);

  useEffect(() => {
    tenorMapRef.current = tenorMap;
  }, [tenorMap]);

  useEffect(() => {
    const wasFrozen = isFrozenRef.current;
    const isNowFrozen = isFrozen;
    if (wasFrozen && !isNowFrozen) {
      if (batchedTickerUpdates.length > 0) {
        batchedTickerUpdates.forEach(ticker => {
          const meta = tickerMeta(ticker, productMapRef.current, tenorMapRef.current);
          if (!meta || !gridRef.current) return;

          const gridApi = gridRef.current.getGridApi();
          const { tenor, productSymbolRoot } = meta;

          const rowNode = gridApi?.getRowNode(tenor.code);
          const midColId = gridApi?.getColumnDef(`${productSymbolRoot}-mid`)?.colId;

          if (!rowNode || !rowNode.data || !midColId) return;

          rowNode.setDataValue(midColId, ticker.mid);
        });
        gridRef.current?.getGridApi()?.refreshClientSideRowModel();
        setBatchedTickerUpdates([]);
      }

      if (batchedOrderBookUpdates.length > 0) {
        batchedOrderBookUpdates.forEach(orderBookTop => {
          const meta = tickerMeta(orderBookTop, productMapRef.current, tenorMapRef.current);
          if (!meta || !gridRef.current) return;

          const gridApi = gridRef.current.getGridApi();
          const { tenor, productSymbolRoot } = meta;

          const rowNode = gridApi?.getRowNode(tenor.code);
          const bidColId = gridApi?.getColumnDef(`${productSymbolRoot}-bid`)?.colId;
          const askColId = gridApi?.getColumnDef(`${productSymbolRoot}-ask`)?.colId;

          if (!rowNode || !rowNode.data || !bidColId || !askColId) return;

          if (Number(orderBookTop.buy.amount) === 0) {
            rowNode.setDataValue(bidColId, null);
          } else {
            rowNode.setDataValue(bidColId, orderBookTop.buy.price);
          }

          if (Number(orderBookTop.sell.amount) === 0) {
            rowNode.setDataValue(askColId, null);
          } else {
            rowNode.setDataValue(askColId, orderBookTop.sell.price);
          }
        });
        gridRef.current?.getGridApi()?.refreshClientSideRowModel();
        setBatchedOrderBookUpdates([]);
      }
    }

    isFrozenRef.current = isFrozen;
  }, [isFrozen, batchedTickerUpdates, batchedOrderBookUpdates]);

  useEffect(() => {
    const debouncedEditWidgetPayloadById = debounce(
      (
        selectedColIds,
        selectedRowIds,
        columnsOrder,
        rollingRowSettings,
        isOverrideRolling,
        selectedBidAskColumns,
        isShowCurrencyUnit,
        columnWidths
      ) => {
        if (isInitialMount.current) {
          isInitialMount.current = false;
          return;
        }
        editWidgetPayloadById(widgetId, {
          selectedColumns: selectedColIds,
          selectedRows: selectedRowIds,
          columnsOrder: columnsOrder,
          userRollingRowSettings: rollingRowSettings,
          isOverrideRolling,
          selectedBidAskColumns,
          isShowCurrencyUnit,
          columnWidths: columnWidths,
        });
      },
      1000
    );

    debouncedEditWidgetPayloadById(
      selectedColIds,
      selectedRowIds,
      columnsOrder,
      rollingRowSettings,
      isOverrideRolling,
      selectedBidAskColumns,
      isShowCurrencyUnit,
      columnWidths
    );

    return () => {
      debouncedEditWidgetPayloadById.cancel();
    };
  }, [selectedColIds, selectedRowIds, columnsOrder, rollingRowSettings, isOverrideRolling, selectedBidAskColumns, isShowCurrencyUnit, columnWidths]);

  useEffect(() => {
    if (selectedUserProducts.length > 0) {
      setForceResubscription(prev => prev + 1);
    }
  }, [selectedUserProducts.length]);

  const tickerCallback = useMemo(() => {
    return (ticker: Ticker) => {
      if (isFrozenRef.current) {
        setBatchedTickerUpdates(prev => [...prev, ticker]);
        return;
      }

      if (!gridRef.current) {
        console.log('this is being called without a grid');
      }

      const meta = tickerMeta(ticker, productMapRef.current, tenorMapRef.current);
      if (!meta || !gridRef.current) return;

      const gridApi = gridRef.current.getGridApi();
      const { tenor, productSymbolRoot, product } = meta;

      if (product.product_type === 'moc') {
        return;
      }

      const rowNode = gridApi?.getRowNode(tenor.code);
      const midColId = gridApi?.getColumnDef(`${productSymbolRoot}-mid`)?.colId;

      if (!rowNode || !rowNode.data || !midColId) return;

      rowNode.setDataValue(midColId, ticker.mid);
      gridApi.refreshClientSideRowModel();
    };
  }, []);

  const memoizedProductSymbols = useMemo(
    () => selectedUserProducts.map(product => product.symbol),
    [selectedUserProducts, columnsOrder, selectedBidAskColumns, isShowCurrencyUnit, columnWidths, forceResubscription]
  );

  useStreamV2Product(tickerCallback, memoizedProductSymbols);

  const orderBookTopCallback = useCallback((orderBookTop: OrderBookTopMessage) => {
    if (isFrozenRef.current) {
      setBatchedOrderBookUpdates(prev => [...prev, orderBookTop]);
      return;
    }

    const meta = tickerMeta(orderBookTop, productMapRef.current, tenorMapRef.current);
    if (!meta || !gridRef.current) return;

    const gridApi = gridRef.current.getGridApi();
    const { tenor, productSymbolRoot, product } = meta;

    const rowNode = gridApi?.getRowNode(tenor.code);
    const bidColId = gridApi?.getColumnDef(`${productSymbolRoot}-bid`)?.colId;
    const askColId = gridApi?.getColumnDef(`${productSymbolRoot}-ask`)?.colId;
    const isMocProduct = product.product_type === ProductType.MOC;
    const mocInvalidTenor = isMocProduct && rowNode?.rowIndex && rowNode.rowIndex > 2;

    if (!rowNode || !rowNode.data || !bidColId || !askColId || mocInvalidTenor) return;
    if (isMocProduct && isAfterSettlementTime(product)) return;

    if (Number(orderBookTop.buy.amount) === 0) {
      rowNode.setDataValue(bidColId, null);
    } else {
      rowNode.setDataValue(bidColId, orderBookTop.buy.price);
    }

    if (Number(orderBookTop.sell.amount) === 0) {
      rowNode.setDataValue(askColId, null);
    } else {
      rowNode.setDataValue(askColId, orderBookTop.sell.price);
    }

    gridApi?.refreshClientSideRowModel();
  }, []);

  const { unsubscribeAllSymbols } = useStreamV2OrderBookTopQuotes(orderBookTopCallback, memoizedProductSymbols);

  const value = useMemo(() => ({ ...gridState }), [gridState]);

  useEffect(() => {
    return () => {
      if (isFirstUnmountRef.current) {
        isFirstUnmountRef.current = false;
        return;
      }

      unsubscribeAllSymbols();
    };
  }, []);

  return <PricesContext.Provider value={value} {...otherProps} />;
}

export function usePricesSettings(): PricingGrid {
  const context = useContext(PricesContext);

  if (!context) {
    throw new Error('usePricesSettings must be used within PricesProvider');
  }

  return context;
}
